import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  OnInit,
  output,
  signal,
  WritableSignal,
} from '@angular/core';
import { EventModel } from '../../event.model';
import { IEventNotificationModel } from '../event-notification.model';
import { EventNotificationsService } from '../event-notifications.service';
import { take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { AppState } from '../../../store/models/app.state';
import { selectListEventNotificationsState } from '../../store/event.state';
import { loadEventNotificationsListByEventId } from '../../store/event.actions';
import { ConfirmService } from '../../../shared/confirm/services/confirm.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EventNotificationComponent } from '../event-notification/event-notification.component';
import { EventNotificationHeaderComponent } from '../event-notification-header/event-notification-header.component';
import { ButtonModule } from '@openng/optimus-ui/button';
import { cloneDeep } from 'lodash-es';

@Component({
  selector: 'phar-event-notifications-list',
  templateUrl: 'event-notifications-list.component.html',
  imports: [EventNotificationComponent, EventNotificationHeaderComponent, TranslatePipe, ButtonModule],
})
export class EventNotificationsListComponent implements OnInit {
  event = input<EventModel>();
  disabled = input(false);
  draftMode = input(false);
  draftNotificationChanged = output<{ isValid: boolean; value: IEventNotificationModel }[]>();
  store = inject(Store<AppState>);
  eventNotificationsService = inject(EventNotificationsService);
  confirmService = inject(ConfirmService);
  translate = inject(TranslateService);
  notifications = this.store.selectSignal(selectListEventNotificationsState);
  draftNotifications: WritableSignal<IEventNotificationModel[]> = signal([]);
  injector: Injector = inject(Injector);
  orderedNotifications = computed(() => {
    const order = {
      before: 0,
      during: 1,
      after: 2,
    };
    return this.notifications().sort((a, b) => {
      return order[a.timing] - order[b.timing];
    });
  });
  displayedNotifications = computed(() => {
    const draftData = this.draftNotifications();
    const orderedData = this.orderedNotifications();
    const isDraftMode = this.draftMode();
    return isDraftMode ? draftData : orderedData;
  });

  draftNotificationsState = signal<{ isValid: boolean; value: IEventNotificationModel }[]>([]);
  draftNotificationsInvalid = computed(() => {
    const state = this.draftNotificationsState();
    return state.some(item => item.isValid === false);
  });
  newNotification: IEventNotificationModel | null = null;

  ngOnInit() {
    if (this.draftMode()) {
      effect(
        () => {
          this.draftNotificationChanged.emit(this.draftNotificationsState());
        },
        {
          injector: this.injector,
        },
      );
    }
  }

  handleDeleteNotification(id: number): void {
    if (!this.draftMode()) {
      return;
    }
    this.draftNotifications.update(arr => {
      return arr.filter(item => item.id !== id);
    });
    this.draftNotificationsState.update(draftNotifications => {
      return draftNotifications.filter(item => item.value.id !== id);
    });
  }

  addNotification(): void {
    if (this.draftMode()) {
      this.draftNotifications.update(draftNotifications => {
        return [
          ...draftNotifications,
          { ...this.newNotification, eventId: new Date().getTime(), id: new Date().getTime() },
        ];
      });

      return;
    }

    if (!this.event()?.id) {
      return;
    }

    if (this.newNotification) {
      this.confirmService.block({
        header: this.translate.instant('STUDY.EVENTS.NOTIFICATIONS.FILL_REQUIRED_FIELDS_MESSAGE'),
        rejectLabel: this.translate.instant('COMMON.GOT_IT'),
        rejectIcon: 'pi pi-check',
        rejectButtonStyleClass: 'p-button-success',
      }).pipe(take(1)).subscribe();

      return;
    }

    this.newNotification = {
      eventId: this.event().id,
      timing: null,
      number: 0,
      unit: null,
      sendTime: null,
      shouldNotifyResponsePerson: true,
    };
  }

  createNewNotification(notification: IEventNotificationModel): void {
    if (this.draftMode()) {
      return;
    }
    this.eventNotificationsService
      .create(notification)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.newNotification = null;
          this.store.dispatch(loadEventNotificationsListByEventId({ id: this.event().id }));
        },
      });
  }

  handleFormValueChanges(data: { value: IEventNotificationModel; isValid: boolean }, id: number) {
    if (!this.draftMode()) {
      return;
    }

    this.draftNotifications.update(draftNotifications => {
      const draftNotificationsCopy = cloneDeep(draftNotifications);
      return draftNotificationsCopy.map((item: IEventNotificationModel) => {
        if (item.id === id) {
          return { ...data.value, id };
        }
        return item;
      });
    });

    this.draftNotificationsState.update(draftNotificationsState => {
      const stateCopy = cloneDeep(draftNotificationsState);

      const index = stateCopy.findIndex(item => item.value.id === id);

      if (index < 0) {
        stateCopy.push({ ...data, value: { ...data.value, id } });
      } else {
        stateCopy[index] = { ...data, value: { ...data.value, id } };
      }
      return stateCopy;
    });
  }
}
